import { useContext, useMemo } from 'react';

import {
  PreparedWorkload,
  useWoopWorkloadFilters,
} from 'features/workload-optimization';

import {
  kindCriterion,
  namespaceCriterion,
} from '../../../_workloads-list/searchCriteria';
import {
  configuredViaCriterion,
  horizontalOptimizationCriterion,
  hpaCriterion,
  jvmCriterion,
  makeAccessCriterion,
  optimizedCriterion,
  predictiveCriterion,
  statusCriterion,
  verticalOptimizationCriterion,
  verticalScalingPolicyCriterion,
} from '../../searchCriteria';
import { OptimizationWorkloadsListContext } from '../../context';

import { makeSearchBoxCriteria } from '@cast/shared-search-ui';

const WORKLOAD_TABLE_SEARCH_WORKLOAD_ID_KEY = 'workloadId';
export const WORKLOAD_TABLE_SEARCH_WORKLOAD_NAME_KEY = 'workloadName';

type Options = {
  // Adds a "Workload type" option to the search box, sharing the `kind` key
  // with the Kind quick-search chip so both UIs render the same selection and
  // a single filter pass. Opt-in so other callers (workloads-list page,
  // predictive drawer) stay unchanged.
  includeWorkloadTypeInSearchBox?: boolean;
};

export const useWorkloadsTableCriteria = (options: Options = {}) => {
  const { data } = useWoopWorkloadFilters();
  const { isNamespaceWritable } = useContext(OptimizationWorkloadsListContext);
  const { includeWorkloadTypeInSearchBox = false } = options;

  return useMemo(
    () => [
      ...makeSearchBoxCriteria<PreparedWorkload>(
        {
          key: WORKLOAD_TABLE_SEARCH_WORKLOAD_ID_KEY,
          title: 'Workload ID:',
          prop: 'id',
          options: data?.workloadIds ?? [],
          allowMultiple: true,
        },
        {
          key: WORKLOAD_TABLE_SEARCH_WORKLOAD_NAME_KEY,
          title: 'Workload name:',
          prop: 'name',
          options: data?.workloadNames ?? [],
          allowMultiple: true,
          matcher: 'exact',
        },
        includeWorkloadTypeInSearchBox && {
          title: 'Workload type:',
          key: 'kind',
          prop: 'kind',
          options: data?.kinds ?? [],
          allowMultiple: true,
          matcher: 'exact',
        }
      ),
      kindCriterion,
      configuredViaCriterion,
      predictiveCriterion,
      namespaceCriterion,
      verticalScalingPolicyCriterion,
      optimizedCriterion,
      hpaCriterion,
      jvmCriterion,
      statusCriterion,
      verticalOptimizationCriterion,
      horizontalOptimizationCriterion,
      makeAccessCriterion(isNamespaceWritable),
    ],
    [data, isNamespaceWritable, includeWorkloadTypeInSearchBox]
  );
};
